const REDACTED = '[REDACTED]';
const REDACTED_MESSAGE = '[REDACTED_MESSAGE]';

const SENSITIVE_KEY_PATTERN =
  /authorization|bearer|token|secret|clientsecret|client_secret|reservationtoken|paymentintentid|payment_intent|password|apikey|api_key/i;

const SENSITIVE_BOOKING_KEY_PATTERN =
  /projectname|projectdescription|description|companyname|specialrequests|packagename|equipmentname/i;

const getObjectKeys = (value) => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? Object.keys(value)
    : []
);

const isErrorLike = (value) => (
  value &&
  typeof value === 'object' &&
  ('message' in value || 'name' in value || 'stack' in value)
);

const describeGraphQLPathForLog = (path) => {
  if (!Array.isArray(path)) return undefined;

  return path.map((entry) => {
    if (typeof entry === 'number') return entry;
    if (typeof entry === 'string' && /^[A-Za-z_][A-Za-z0-9_]*$/.test(entry)) {
      return entry;
    }
    return REDACTED;
  });
};

const describeGraphQLErrorExtensionsForLog = (extensions) => {
  if (!extensions || typeof extensions !== 'object' || Array.isArray(extensions)) {
    return undefined;
  }

  return {
    keys: getObjectKeys(extensions),
    hasCode: Boolean(extensions.code),
    hasErrorType: Boolean(extensions.errorType),
    hasClassification: Boolean(extensions.classification),
    hasStatusCode: Boolean(extensions.statusCode),
    statusCode: typeof extensions.statusCode === 'number' ? extensions.statusCode : undefined,
  };
};

export const isBookingDiagnosticsEnabled = () => (
  process.env.NODE_ENV !== 'production' &&
  process.env.REACT_APP_DEBUG_BOOKING_LOGS === 'true'
);

export const describeErrorForLog = (error) => {
  if (!error) return null;

  return {
    hasName: Boolean(error?.name),
    message: error?.message ? REDACTED_MESSAGE : undefined,
    hasStack: Boolean(error?.stack),
    hasCode: Boolean(error?.code),
    hasType: Boolean(error?.type),
    hasStatusCode: error?.statusCode != null,
    statusCode: typeof error?.statusCode === 'number' ? error.statusCode : undefined,
  };
};

export const describeApolloErrorForLog = (error) => ({
  ...describeErrorForLog(error),
  graphQLErrors: Array.isArray(error?.graphQLErrors)
    ? error.graphQLErrors.map((graphQLError) => ({
      message: graphQLError?.message ? REDACTED_MESSAGE : undefined,
      path: describeGraphQLPathForLog(graphQLError?.path),
      hasErrorType: Boolean(graphQLError?.errorType),
      extensions: describeGraphQLErrorExtensionsForLog(graphQLError?.extensions),
    }))
    : undefined,
  networkError: error?.networkError
    ? {
      ...describeErrorForLog(error.networkError),
    }
    : undefined,
  clientErrorCount: Array.isArray(error?.clientErrors) ? error.clientErrors.length : undefined,
  hasExtraInfo: Boolean(error?.extraInfo),
});

export const describeBookingInputForLog = (input = {}) => ({
  hasStudioID: Boolean(input?.studioID),
  hasSlotID: Boolean(input?.slotID),
  hasReservationToken: Boolean(input?.reservationToken),
  hasPaymentIntentID: Boolean(input?.paymentIntentID),
  hasStartDateTime: Boolean(input?.startDateTime),
  hasEndDateTime: Boolean(input?.endDateTime),
  hasProjectName: Boolean(input?.projectName),
  projectNameLength: typeof input?.projectName === 'string' ? input.projectName.length : undefined,
  hasProjectDescription: Boolean(input?.projectDescription),
  projectDescriptionLength:
    typeof input?.projectDescription === 'string' ? input.projectDescription.length : undefined,
  hasCompanyName: Boolean(input?.companyName),
  companyNameLength: typeof input?.companyName === 'string' ? input.companyName.length : undefined,
  creativePackageCount: Array.isArray(input?.creativePackages) ? input.creativePackages.length : 0,
  bookedEquipmentCount: Array.isArray(input?.bookedEquipment) ? input.bookedEquipment.length : 0,
  hasPackageId: Boolean(input?.packageId),
  hasPackageName: Boolean(input?.packageName),
  packageDurationHours:
    typeof input?.packageDurationHours === 'number' ? input.packageDurationHours : undefined,
  numberOfGuests:
    typeof input?.numberOfGuests === 'number' ? input.numberOfGuests : undefined,
});

export const describeReservationForLog = (reservation = {}) => ({
  hasSlotID: Boolean(reservation?.slotID),
  hasReservationToken: Boolean(reservation?.reservationToken),
  hasClientSecret: Boolean(reservation?.clientSecret),
  hasPaymentIntentID: Boolean(reservation?.paymentIntentID),
  hasExpiresAt: Boolean(reservation?.expiresAt),
  usesConnect: typeof reservation?.usesConnect === 'boolean' ? reservation.usesConnect : undefined,
  hasStripeConnectAccountId: Boolean(reservation?.stripeConnectAccountId),
  hasPayoutMode: Boolean(reservation?.payoutMode),
  hasTotalAmountCents: typeof reservation?.totalAmountCents === 'number',
  hasOriginalAmountCents: typeof reservation?.originalAmountCents === 'number',
  hasCampaignDiscountAmountCents: typeof reservation?.campaignDiscountAmountCents === 'number',
  campaignApplied: typeof reservation?.campaignApplied === 'boolean' ? reservation.campaignApplied : undefined,
});

export const describeMutationDataForLog = (data) => ({
  hasData: Boolean(data),
  keys: getObjectKeys(data),
});

export const describeBookingResultForLog = (result = {}) => ({
  success: Boolean(result?.success),
  hasBooking: Boolean(result?.booking),
  hasBookingID: Boolean(result?.booking?.id),
  hasPaymentIntentID: Boolean(result?.booking?.paymentIntentID),
  hasPaymentStatus: Boolean(result?.booking?.paymentStatus),
});

export function sanitizeDiagnosticValue(value, key = '') {
  if (value == null) return value;

  if (SENSITIVE_KEY_PATTERN.test(key)) {
    return REDACTED;
  }

  if (SENSITIVE_BOOKING_KEY_PATTERN.test(key)) {
    return typeof value === 'string' ? REDACTED : sanitizeDiagnosticValue(value);
  }

  if (typeof value === 'string') {
    if (value === REDACTED || value === REDACTED_MESSAGE) return value;
    return REDACTED;
  }
  if (typeof value !== 'object') return value;
  if (isErrorLike(value)) return describeErrorForLog(value);

  if (Array.isArray(value)) {
    return {
      count: value.length,
    };
  }

  return Object.fromEntries(
    Object.entries(value).map(([entryKey, entryValue]) => [
      entryKey,
      sanitizeDiagnosticValue(entryValue, entryKey),
    ])
  );
}

export const logBookingDiagnostic = (scope, message, details = {}) => {
  if (!isBookingDiagnosticsEnabled()) return;

  console.error(`${scope} ${message}`, sanitizeDiagnosticValue(details));
};
